import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { getEventsList, getUserSelectedEvents } from '@/routes/trackings/tracking-utils';
import { IntegrationV2Query, IntegrationTypeV2, SendlaneCredentialsModel } from '@/gql/graphql';
import { useParams } from 'react-router';
import { useEffect } from 'react';
import { SaveButton } from '../../components/save-button';
import {
  FormLayout,
  FormSection,
  PasswordField
} from './../../components/tracking-details-form/base';
import {
  ConsentModeSection,
  FormHeaderSection,
  CustomEventsSection
} from '../../components/tracking-details-form/shared-sections';
import { useIntegrationStore } from '../../stores/tracking-store';
import { useApolloClient } from '@apollo/client';

const formSchema = z.object({
  credentials: z.object({
    apiKey: z.string().min(1, 'API Key is required.'),
    integrationToken: z.string().min(1, 'Integration Token is required.')
  }),
  consentMode: z.boolean(),
  customEvents: z.array(z.string()).optional()
});

const eventsList = getEventsList(IntegrationTypeV2.Sendlane);

export const SendlaneSettingsForm = ({ onContinue }: { onContinue: () => void }) => {
  const { toast } = useToast();
  const params = useParams<{ id: string; slug: string }>();
  const { integration, isLoading, updateSettings } = useIntegrationStore();
  const client = useApolloClient();

  const isInitialSetup =
    !(integration?.credentials as SendlaneCredentialsModel | undefined)?.apiKey &&
    !(integration?.credentials as SendlaneCredentialsModel | undefined)?.integrationToken;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      credentials: {
        apiKey: '',
        integrationToken: ''
      },
      consentMode: false,
      customEvents: []
    }
  });

  useEffect(() => {
    if (!isLoading && integration) {
      const credentials = integration.credentials as SendlaneCredentialsModel | undefined;
      form.reset({
        credentials: {
          apiKey: credentials?.apiKey ?? '',
          integrationToken: credentials?.integrationToken ?? ''
        },
        consentMode: integration.consentMode ?? false,
        customEvents: getUserSelectedEvents({
          integrationV2: integration as IntegrationV2Query['integrationV2']
        })
      });
    }
  }, [integration, form.reset]);

  const { isDirty } = form.formState;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await updateSettings(client, {
        id: params.id!,
        credentials: values.credentials,
        customEvents: values.customEvents ?? [],
        consentMode: values.consentMode
      });
      toast({
        title: 'Form submitted',
        description: 'Your Sendlane integration settings have been saved.',
        variant: 'default'
      });
      onContinue();
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to save Sendlane integration settings.',
        variant: 'destructive'
      });
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <FormLayout>
          <FormHeaderSection title="Sendlane Settings">
            In order to send transactions data to this destination, we need the following
            information from your Sendlane account.
          </FormHeaderSection>
          <FormSection columns={2}>
            <PasswordField form={form} name="apiKey" label="Sendlane Private API Key" />
            <PasswordField form={form} name="integrationToken" label="Sendlane Integration Token" />
          </FormSection>
          <ConsentModeSection form={form} />
          <CustomEventsSection
            form={form}
            eventsList={eventsList}
            getUserSelectedEvents={() =>
              getUserSelectedEvents({
                integrationV2: integration as IntegrationV2Query['integrationV2']
              })
            }
          />
          <FormSection>
            <SaveButton isDirty={isDirty} isUpdating={isLoading} isInitialSetup={isInitialSetup} />
          </FormSection>
        </FormLayout>
      </form>
    </Form>
  );
};
